/**
 * Vorlage „Fett in der Pfanne erhitzen“.
 *
 * Öl läuft in die Pfanne auf dem Herd und wird durch Schwenken verteilt, oder
 * ein Stück Butter schmilzt und schäumt auf. Mit steigender Hitze flimmert die
 * Oberfläche; am Stiel eines Holzlöffels steigen kleine Bläschen auf – das
 * Fett ist heiß genug.
 */
import { ease, lerp, segment, wave } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { roundedRectPath } from '../paths.ts';
import { compact, ellipse, faded, group, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { bubbles, heatWaves, surfaceLine } from '../parts/effects.ts';
import { pourer, stream } from '../parts/kitchen.ts';
import { arcArrow, arrow, counter, doneMark } from '../parts/stage.ts';
import { GRATE_Y, HEAT_LEVELS, HEAT_POWER, heatFromText, stove, stoveKnob } from '../parts/stove.ts';
import type { HeatLevel } from '../parts/stove.ts';
import { place, woodenSpoon } from '../parts/utensils.ts';
import { pan } from '../parts/vessels.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

const PAN = { cx: 96, width: 124 } as const;
const RIM_Y = GRATE_Y - 13 - PAN.width * 0.1;
const KNOB_X = 214;
/** Länge des Holzlöffels in dieser Größe (Mulde bis Stielende). */
const SPOON_LENGTH = 86 * 0.8;
/** Ausgussecke der Ölflasche bzw. des Kännchens. */
const LIP = { x: 140, y: 34 } as const;

/** Zeitplan. */
const TIME = {
  cupIn: [0.02, 0.08],
  tilt: [0.08, 0.14],
  pour: [0.12, 0.3],
  untilt: [0.28, 0.34],
  cupOut: [0.32, 0.38],
  swirl: [0.32, 0.5],
  heat: [0.3, 0.7],
  test: [0.62, 0.84],
  done: [0.78, 0.82],
  reset: [0.88, 1],
} as const;

/** Fette. */
const FATS = { oil: 'Öl', butter: 'Butter' } as const;
type Fat = keyof typeof FATS;

/** Vorlage „Fett in der Pfanne erhitzen“. */
export const stoveHeatFatTemplate = defineTemplate({
  id: 'stove.heat-fat',
  group: 'stove',
  title: 'Fett in der Pfanne erhitzen',
  description:
    'Öl oder Butter kommt in die Pfanne und wird erhitzt. Das Fett ist heiß genug, wenn es flimmert und am Stiel eines Holzlöffels kleine Bläschen aufsteigen.',
  durationSeconds: 6,
  keywords: [
    'erhitzen',
    'öl erhitzen',
    'fett erhitzen',
    'butter erhitzen',
    'butterschmalz',
    'in der pfanne erhitzen',
    'in einer pfanne erhitzen',
    'pfanne erhitzen',
    'öl in einer pfanne',
    'öl in der pfanne',
    'fett in der pfanne',
    'butter in der pfanne',
    'heiß werden lassen',
  ],
  stillTime: 0.74,
  parameters: [choice('fat', 'Fett', FATS, 'oil'), choice('heat', 'Hitze', HEAT_LEVELS, 'medium')],
  inferValues(text) {
    const lower = text.toLowerCase();
    return {
      ...heatFromText(text),
      fat: /butter/.test(lower) ? 'butter' : /öl\b|oel|fett|schmalz/.test(lower) ? 'oil' : undefined,
    };
  },
  scene(values, t) {
    const fat = textValue(values, 'fat', 'oil') as Fat;
    const heat = textValue(values, 'heat', 'medium') as HeatLevel;
    const power = HEAT_POWER[heat] ?? HEAT_POWER.medium;
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const vessel = pan(PAN.cx, RIM_Y, PAN.width);
    const burner = stove(PAN.cx, power, t);
    const floorY = RIM_Y + 3;
    const hot = ease.inOutSine(segment(t, ...TIME.heat)) * (1 - reset);

    // Menge des Fetts in der Pfanne: Öl läuft ein und wird verteilt, Butter schmilzt.
    let spread: number;
    let extra: SceneNode[] = [];
    let above: (SceneNode | null)[] = [];
    if (fat === 'oil') {
      const poured = ease.outQuad(segment(t, ...TIME.pour));
      const swirled = ease.inOutSine(segment(t, ...TIME.swirl));
      spread = lerp(0.25 * poured, 1, swirled) * (1 - reset);
      const flow =
        segment(t, TIME.pour[0], TIME.pour[0] + 0.02) * (1 - segment(t, TIME.pour[1] - 0.02, TIME.pour[1]));
      const tilt =
        -75 * (ease.inOutCubic(segment(t, ...TIME.tilt)) - ease.inOutCubic(segment(t, ...TIME.untilt)));
      const cup = ease.outCubic(segment(t, ...TIME.cupIn)) * (1 - segment(t, ...TIME.cupOut));
      above = [
        flow > 0.02
          ? stream(
              [LIP, { x: LIP.x - 6, y: LIP.y + 24 }, { x: PAN.cx + 18, y: floorY }],
              4 * flow,
              t,
              'shade',
            )
          : null,
        faded(cup, [pourer(LIP, 22, 34, tilt, lerp(0.8, 0.2, segment(t, ...TIME.pour)), 'shade')]),
      ];
    } else {
      // Butter: das Stück wird flacher, die Pfütze wächst.
      const melt = ease.inOutSine(segment(t, 0.06, 0.5)) * (1 - reset);
      spread = melt;
      const width = lerp(30, 4, melt);
      const height = lerp(14, 1, melt);
      extra =
        melt < 0.97
          ? [
              shape(
                roundedRectPath(PAN.cx - width / 2, floorY - height, width, height, 2 + melt * 3),
                styles.solid('tint'),
              ),
            ]
          : [];
    }

    // Beim Schwenken kippt die Pfanne leicht hin und her.
    const swirlPhase = fat === 'oil' ? segment(t, ...TIME.swirl) : 0;
    const sway = Math.sin(swirlPhase * Math.PI) * wave(swirlPhase, 2) * 5;
    const rx = (vessel.rx - 10) * Math.max(0.12, spread);
    const ry = vessel.ry * 0.6 * Math.max(0.12, spread);
    const shimmer = hot > 0.3 && spread > 0.5;
    const film =
      spread > 0.02
        ? group(
            compact([
              ellipse(PAN.cx, floorY, rx, ry, { fill: 'shade', stroke: 'ink', strokeWidth: STROKE.fine }),
              shimmer
                ? shape(surfaceLine(PAN.cx - rx * 0.6, floorY - 2, rx * 1.2, 0.8 * hot, t * 3, 3), {
                    stroke: 'paper',
                    strokeWidth: STROKE.fine,
                  })
                : null,
              ...(fat === 'butter' ? bubbles(PAN.cx - rx, floorY - 4, rx * 2, 5, 0.6 * hot, t, 4) : []),
            ]),
            { clip: vessel.opening },
          )
        : null;

    // Holzlöffelprobe: Stiel senkrecht ins Fett, Bläschen steigen am Stiel auf.
    const spoon =
      fat === 'oil'
        ? segment(t, TIME.test[0], TIME.test[0] + 0.04) * (1 - segment(t, TIME.test[1] - 0.04, TIME.test[1]))
        : 0;
    const testBubbles = spoon > 0.5 ? bubbles(PAN.cx - 14, floorY - 8, 12, 8, 0.5, t, 12) : [];
    const done = ease.outBack(segment(t, ...TIME.done)) * (1 - segment(t, 0.86, 0.88));

    return compact([
      ...counter(),
      ...burner.back,
      group([...vessel.back, film, ...extra], {
        transform: { rotate: sway, ox: PAN.cx, oy: RIM_Y },
      }),
      ...heatWaves(PAN.cx, RIM_Y - 12, t, hot * 0.9, 3, 30),
      faded(spoon, [
        // Der Löffel steht mit dem Stielende im Fett.
        group([place(PAN.cx - 8, floorY + 1 - SPOON_LENGTH, 180, woodenSpoon(), 0.8)]),
        ...testBubbles,
      ]),
      group(vessel.front, { transform: { rotate: sway, ox: PAN.cx, oy: RIM_Y } }),
      ...burner.front,
      stoveKnob(KNOB_X, power),
      ...above,
      faded(done, [doneMark(200, 40, 1.1)]),
    ]);
  },
  hints(values) {
    const fat = textValue(values, 'fat', 'oil') as Fat;
    return fat === 'oil'
      ? [arcArrow(PAN.cx, RIM_Y - 6, 50, 12, 200, 340)]
      : [
          arrow([
            { x: PAN.cx, y: RIM_Y - 44 },
            { x: PAN.cx, y: RIM_Y - 16 },
          ]),
        ];
  },
});
